import isEqual from 'lodash.isequal';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLanguage } from 'context/AppContext';
import API from 'api/API';

export const mediaStreamConstraintsHD: MediaStreamConstraints = {
  video: {
    width: { min: 1280, ideal: 1920, max: 3840 },
    height: { min: 720, ideal: 1080, max: 2160 },
    //aspectRatio: { ideal: 1.7777777778 },
    // @ts-ignore - This is valid
    focusMode: 'continuous',
    frameRate: { ideal: 20, max: 30 },
  },
  audio: false,
};

export const useCameras = (mediaStreamConstraints?: MediaStreamConstraints) => {
  const lang = useLanguage()[0].TrayImageCapture;

  const [cameraIdx, setCameraIdx] = useState<number>(0);
  const [cameras, setCameras] = useState<MediaDeviceInfo[] | undefined>();
  const [negotiatedResolutions, setNegotiatedResolutions] = useState<Record<string, { width: number; height: number }>>(
    {},
  );
  const havePermission = useRef<boolean>(false);

  // detect available cameras
  const detectCameras = useMemo(
    () => async () => {
      if (cameras?.[0]?.deviceId) return;

      const _detectCameras = async () => {
        const mediaDevices = await navigator.mediaDevices.enumerateDevices();
        const _cameras = mediaDevices.filter(({ kind }) => kind == 'videoinput');

        // if no cameras then error
        if (!_cameras.length) {
          API.errorHandler(new Error(lang.noCamerasAvailable));
        }

        if (isEqual(cameras, _cameras)) return;

        setCameras(_cameras);

        // try to select the back camera
        // first look for a camera with 'back' in the label, the back camera is
        //  the main/default camera and it usually doesnt contain 'back' in the label
        const backCameraIndex = _cameras.findIndex(({ label }) => label?.toLowerCase().includes('back'));
        const hasBackCamera = backCameraIndex != -1;

        if (hasBackCamera) {
          setCameraIdx(backCameraIndex);
        } else {
          // more commonly, the front camera may contain the word 'front' in the label
          const maybeBackCameraIndex = _cameras.findIndex(
            ({ label }) => label && !label.toLowerCase().includes('front'),
          );
          if (maybeBackCameraIndex != -1) setCameraIdx(maybeBackCameraIndex);
        }
      };

      if (havePermission.current || !navigator.mediaDevices.getUserMedia) {
        await _detectCameras();
        return;
      }

      try {
        // check if camera access is allowed, if not then notify
        const stream = await navigator.mediaDevices.getUserMedia(mediaStreamConstraints || { video: true });
        const resolutions: Record<string, { width: number; height: number }> = {};

        stream.getTracks().forEach(track => {
          const settings = track.getSettings();
          if (settings.width && settings.height && settings.deviceId) {
            resolutions[settings.deviceId] = { width: settings.width, height: settings.height };
          }
          track.stop();
        });

        setNegotiatedResolutions(prev => ({ ...prev, ...resolutions }));
        havePermission.current = true;
        await _detectCameras();
      } catch (err) {
        const errorName = (err as DOMException).name;
        if (errorName == 'NotAllowedError') {
          API.errorHandler(new Error(lang.cameraAccessHasBeenDenied));
        } else if (errorName == 'NotFoundError') {
          API.errorHandler(new Error(lang.noCamerasAvailable));
        } else {
          API.errorHandler(err);
        }
      }
    },
    [cameras, lang.cameraAccessHasBeenDenied, lang.noCamerasAvailable, mediaStreamConstraints],
  );

  // look for available cameras on load
  useEffect(() => {
    if (!cameras) detectCameras();
  }, [cameras, detectCameras]);

  const switchCamera = useCallback(() => {
    let idx = cameraIdx + 1;
    if (idx == cameras?.length) idx = 0;
    setCameraIdx(idx);
  }, [cameraIdx, cameras?.length]);

  const camera = cameras?.[cameraIdx];
  const negotiatedResolution = camera?.deviceId ? negotiatedResolutions[camera.deviceId] : undefined;

  return {
    detectCameras,
    switchCamera,
    cameras,
    camera,
    negotiatedResolution,
  };
};

export default useCameras;
